import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { X, Plus, MessageSquare, CheckCircle2, Clock } from 'lucide-react'
import { MessageList } from './MessageList'
import { ChatInput } from './ChatInput'
import { useProject } from '@/hooks/useVRPData'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Checkbox } from '@/components/ui/checkbox'
import { CompartmentItemsManager } from '@/components/ui/input'
import type { Id } from '../../../../convex/_generated/dataModel'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useState, useMemo, useEffect } from 'react'
import { toast } from 'sonner'
import { cn, generateConversationTitle, formatRelativeTime } from '@/lib/utils'

interface AgentChatPanelProps {
  projectId: string
  onClose?: () => void
}

interface OptimisticMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  isOptimistic?: boolean
}

export function AgentChatPanel({ projectId, onClose }: AgentChatPanelProps) {
  // CRITICAL: Validate required props (zero-defect enforcement)
  if (!projectId) {
    throw new Error('AgentChatPanel: projectId is required')
  }

  // LEVER: Extend existing useProject hook instead of creating new hook
  const project = useProject(projectId as Id<'projects'>)
  const conversations = project?.agentConversations || []

  // Active session state - initialize with first conversation or create new session
  const [activeSessionId, setActiveSessionId] = useState<string>(() => {
    if (conversations.length > 0) {
      return conversations[0].sessionId
    }
    return `session_${Date.now()}`
  })

  // Find active conversation - explicit error handling (no fallbacks)
  const activeConversation = conversations.find(
    c => c.sessionId === activeSessionId
  )

  // Get messages for active conversation
  // If no conversation found AND it's a new session, use empty array
  // If no conversation found AND it's NOT a new session, that's an error
  const isNewSession = !conversations.some(c => c.sessionId === activeSessionId)
  const dbMessages = (() => {
    if (activeConversation) {
      return activeConversation.messages
    }
    if (isNewSession) {
      // New session - empty message array is expected
      return []
    }
    // Session not found and it's not new - this is an error state
    throw new Error(
      `AgentChatPanel: Session ${activeSessionId} not found in conversations`
    )
  })()

  // Diagnostic: Log when database messages update
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.error(
        `[AgentChat] 📊 Database messages updated: ${dbMessages.length} messages`
      )
    }
  }, [dbMessages.length])

  // Manual loading state (actions don't auto-track like mutations)
  const [isLoading, setIsLoading] = useState(false)

  // Optimistic message state for instant UI feedback
  const [optimisticMessage, setOptimisticMessage] =
    useState<OptimisticMessage | null>(null)

  // Selection mode state for conversation deletion
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(
    new Set()
  )

  // Inline compartment form state (Task 2: Dual-mode vehicle creation)
  const [isCompartmentFormVisible, setIsCompartmentFormVisible] =
    useState(false)
  const [compartmentItems, setCompartmentItems] = useState<any[]>([])
  const [vehicleDescription, setVehicleDescription] = useState('New Vehicle')

  // Combine database messages with optimistic message
  // Only show optimistic message if it's not already in dbMessages
  const messages = useMemo(() => {
    if (optimisticMessage) {
      // Check if the optimistic message is already in the database
      const lastDbMessage = dbMessages[dbMessages.length - 1]
      const isOptimisticMessagePersisted =
        lastDbMessage &&
        lastDbMessage.role === 'user' &&
        lastDbMessage.content === optimisticMessage.content &&
        Math.abs(lastDbMessage.timestamp - optimisticMessage.timestamp) < 5000 // Within 5 seconds

      // If optimistic message is already persisted, don't show it again
      if (isOptimisticMessagePersisted) {
        return dbMessages
      }

      // Otherwise, show optimistic message at the end
      return [...dbMessages, optimisticMessage]
    }
    return dbMessages
  }, [dbMessages, optimisticMessage])

  // Cleanup: Auto-clear optimistic messages after 30 seconds (prevents stale messages)
  useEffect(() => {
    if (optimisticMessage) {
      const timer = setTimeout(() => {
        if (import.meta.env.DEV) {
          console.error(
            '[AgentChat] ⚠️  Clearing stale optimistic message after 30 seconds'
          )
        }
        setOptimisticMessage(null)
      }, 30000) // 30 seconds

      return () => clearTimeout(timer)
    }
  }, [optimisticMessage])

  // Use useAction for backend action (agent uses Claude API via action)
  const chatWithAgent = useAction(api.agents.configurationAgent.chatWithAgent)
  const deleteConversations = useMutation(api.projects.deleteAgentConversations)
  const createVehicle = useMutation(api.vehicles.create)

  // Query products for compartment form dropdown
  const products = useQuery(api.projects.listProductsByProject, {
    projectId: projectId as Id<'projects'>,
  })

  // Selection mode handlers
  const handleToggleSelection = () => {
    setIsSelectionMode(!isSelectionMode)
    if (isSelectionMode) {
      // Exiting selection mode - clear selections
      setSelectedSessions(new Set())
    }
  }

  const handleSelectSession = (sessionId: string) => {
    const newSelected = new Set(selectedSessions)
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId)
    } else {
      newSelected.add(sessionId)
    }
    setSelectedSessions(newSelected)
  }

  const handleDeleteSelected = async () => {
    if (selectedSessions.size === 0) {
      if (import.meta.env.DEV) {
        console.error('[AgentChat] Delete called with no selections')
      }
      return
    }

    try {
      await deleteConversations({
        id: projectId as Id<'projects'>,
        sessionIds: Array.from(selectedSessions),
      })

      // If active session was deleted, switch to newest remaining
      if (selectedSessions.has(activeSessionId)) {
        const remaining = conversations.filter(
          c => !selectedSessions.has(c.sessionId)
        )
        if (remaining.length > 0) {
          // Sort to get newest
          const newest = [...remaining].sort(
            (a, b) => b.lastUpdatedAt - a.lastUpdatedAt
          )[0]
          setActiveSessionId(newest.sessionId)
        } else {
          // No conversations left - create new session
          setActiveSessionId(`session_${Date.now()}`)
        }
      }

      setIsSelectionMode(false)
      setSelectedSessions(new Set())
      toast.success(`Deleted ${selectedSessions.size} conversation(s)`)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to delete: ${errorMessage}`)
      if (import.meta.env.DEV) {
        console.error('[AgentChat] Delete failed:', error)
      }
    }
  }

  // Inline compartment form save handler (Task 2: Form launcher mode)
  const handleSaveCompartments = async () => {
    if (compartmentItems.length === 0) {
      toast.error('Please add at least one compartment before saving')
      return
    }

    try {
      // Create vehicle with compartments - backend auto-generates capacity array
      await createVehicle({
        projectId: projectId as Id<'projects'>,
        description: vehicleDescription,
        compartments: compartmentItems, // Backend handles capacity calculation
      })

      // Close inline form and reset state
      setIsCompartmentFormVisible(false)
      setCompartmentItems([])
      setVehicleDescription('New Vehicle')

      // Show success toast
      toast.success(`Vehicle "${vehicleDescription}" created successfully`)

      // Optional: Send confirmation message to chat
      if (import.meta.env.DEV) {
        console.log('[AgentChat] Vehicle created via inline compartment form')
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to create vehicle: ${errorMessage}`)
      if (import.meta.env.DEV) {
        console.error('[AgentChat] Vehicle creation failed:', error)
      }
    }
  }

  const handleSend = async (message: string) => {
    if (!message.trim()) {
      if (import.meta.env.DEV) {
        console.error('[AgentChat] Empty message blocked')
      }
      return // Don't throw - just return silently
    }

    // Production-safe diagnostic logging
    if (import.meta.env.DEV) {
      console.error(
        '[AgentChat] 🟢 Starting message send:',
        message.substring(0, 50) + '...'
      )
    }

    // Track initial message count for save verification
    const initialMessageCount = dbMessages.length
    if (import.meta.env.DEV) {
      console.error(`[AgentChat] Initial message count: ${initialMessageCount}`)
    }

    // Optimistic UI update - show user message immediately
    setOptimisticMessage({
      role: 'user',
      content: message,
      timestamp: Date.now(),
      isOptimistic: true,
    })

    setIsLoading(true)

    try {
      if (import.meta.env.DEV) {
        console.error('[AgentChat] 🟡 Calling chatWithAgent action...')
      }
      const startTime = Date.now()

      const result = await chatWithAgent({
        projectId: projectId as Id<'projects'>,
        message: message,
        sessionId: activeSessionId, // ✅ CRITICAL FIX: Pass active session ID to preserve conversation history
      })

      const duration = Date.now() - startTime
      if (import.meta.env.DEV) {
        console.error(
          `[AgentChat] 🟢 Message sent successfully in ${duration}ms`,
          result
        )
        console.error(
          '[AgentChat] ✅ Backend confirmed database save - trusting optimistic UI'
        )
      }

      // ✅ NEW: Detect form launcher action from backend (Task 2: Dual-mode vehicle creation)
      // The backend returns a special action when Claude uses form mode
      // Extract and parse the response content to check for action
      const response = result.response
      if (Array.isArray(response)) {
        for (const block of response) {
          if (block.type === 'tool_use' && block.name === 'createVehicles') {
            // Tool execution result is in the conversation messages
            // We'll detect the action from the tool result in the message history
            // For now, check if the last assistant message contains form launch indicator
            if (import.meta.env.DEV) {
              console.log('[AgentChat] Tool use detected:', block)
            }
          }
          if (block.type === 'text') {
            // Check if text indicates form launch
            const text = block.text.toLowerCase()
            if (
              text.includes('opening vehicle form') ||
              text.includes('compartment configuration')
            ) {
              if (import.meta.env.DEV) {
                console.log('[AgentChat] Form launch detected in response text')
              }
              // Show inline compartment form in chat
              setIsCompartmentFormVisible(true)
              setCompartmentItems([]) // Start with empty compartments
              setVehicleDescription('New Vehicle') // Default name
            }
          }
        }
      }

      // ✅ Trust backend save + Convex real-time sync
      // Backend logs show save succeeded: "✅ Conversation saved successfully to database"
      // Convex will reactively update the query when data changes
      // Optimistic message will automatically disappear when real message arrives (handled by useMemo)

      // No verification needed - let Convex real-time sync handle the transition
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AgentChat] 🔴 Failed to send message:', error)
        console.error(
          '[AgentChat] 🔴 Error type:',
          error instanceof Error ? error.constructor.name : typeof error
        )
        console.error(
          '[AgentChat] 🔴 Error message:',
          error instanceof Error ? error.message : String(error)
        )
      }

      // Clear optimistic message on error
      setOptimisticMessage(null)

      // Show error toast to user with specific error message
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred'
      toast.error(`Failed to send message: ${errorMessage}`)

      // Don't re-throw - we've already shown the error to the user
    } finally {
      setIsLoading(false)
      if (import.meta.env.DEV) {
        console.error(
          '[AgentChat] 🔵 handleSend completed, isLoading set to false'
        )
      }
    }
  }

  return (
    <Sheet open={true} onOpenChange={() => onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:w-[50vw] sm:min-w-[640px] sm:max-w-[1600px] p-0 flex h-full"
        data-slot="agent-chat-panel"
      >
        {/* Conversation Sidebar - 320px fixed width (40 × 8pt) */}
        <div className="w-[320px] border-r border-border flex flex-col bg-muted">
          {/* Header with Close Button - 64px height */}
          <div className="h-16 flex items-center justify-between p-4 border-b border-border">
            <SheetTitle className="text-lg font-semibold">
              AI Assistant
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onClose?.()}
              className="h-8 w-8 p-0"
              aria-label="Close AI Assistant"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* New Chat Button - 40px height */}
          <div className="p-4">
            <Button
              onClick={() => setActiveSessionId(`session_${Date.now()}`)}
              className="w-full h-10 gap-2"
            >
              <Plus className="h-4 w-4" />
              New Chat
            </Button>
          </div>

          {/* Selection Mode Toolbar */}
          <div className="flex items-center gap-2 px-4 pb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleSelection}
              className="flex-1"
            >
              {isSelectionMode ? 'Cancel' : 'Select'}
            </Button>

            {isSelectionMode && selectedSessions.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
                className="flex-1"
              >
                Delete ({selectedSessions.size})
              </Button>
            )}
          </div>

          {/* Conversation List - Scrollable */}
          <ScrollArea className="flex-1">
            <div className="px-2 pb-4 flex flex-col gap-2">
              {conversations.length === 0 && (
                <div className="text-sm text-muted-foreground px-4 py-8 text-center">
                  No conversations yet
                </div>
              )}
              {[...conversations]
                .sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt) // ✅ NEWEST FIRST
                .map(conv => {
                  // ✅ FIX: Validate conversation has messages (zero-defect: no fallback)
                  if (!conv.messages || conv.messages.length === 0) {
                    throw new Error(
                      `Conversation ${conv.sessionId} has no messages. Database integrity error.`
                    )
                  }

                  // ✅ FIX: Validate first message has content (defensive against undefined)
                  const firstMessage = conv.messages[0]?.content || ''
                  const title = generateConversationTitle(firstMessage)
                  const isActive = conv.sessionId === activeSessionId

                  // Get last message timestamp for relative time
                  const lastMessage = conv.messages[conv.messages.length - 1]
                  const relativeTime = formatRelativeTime(lastMessage.timestamp)

                  // Determine conversation status based on last message and content
                  const hasRecordLinks = conv.messages.some(
                    m => m.recordLinks && m.recordLinks.length > 0
                  )
                  const lastMessageIsAssistant =
                    lastMessage.role === 'assistant'
                  const conversationStatus = hasRecordLinks
                    ? 'completed'
                    : lastMessageIsAssistant
                      ? 'waiting'
                      : 'in_progress'

                  // Count created records across all messages
                  const recordCount = conv.messages.reduce(
                    (sum, m) => sum + (m.recordLinks?.length || 0),
                    0
                  )

                  return (
                    <button
                      key={conv.sessionId}
                      onClick={() => {
                        if (isSelectionMode) {
                          // In selection mode - toggle selection
                          handleSelectSession(conv.sessionId)
                        } else {
                          // Normal mode - switch to session
                          if (import.meta.env.DEV) {
                            console.error(
                              `[AgentChat] 📌 Switching to session: ${conv.sessionId}`
                            )
                            console.error(
                              `[AgentChat] Message count: ${conv.messages.length}`
                            )
                          }
                          setActiveSessionId(conv.sessionId)
                        }
                      }}
                      className={cn(
                        'w-full text-left px-4 py-3 rounded-lg transition-all',
                        'flex flex-col gap-2 min-h-[72px]',
                        'border border-transparent',
                        isActive
                          ? 'bg-accent text-accent-foreground border-accent-foreground/20 shadow-sm'
                          : 'text-foreground hover:bg-accent/50 hover:border-accent-foreground/10'
                      )}
                    >
                      {/* Title and Status Row */}
                      <div className="flex items-start gap-2">
                        {isSelectionMode && (
                          <Checkbox
                            checked={selectedSessions.has(conv.sessionId)}
                            onCheckedChange={() =>
                              handleSelectSession(conv.sessionId)
                            }
                            onClick={e => e.stopPropagation()}
                            className="mt-0.5"
                          />
                        )}
                        <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span
                          className={cn(
                            'flex-1 text-sm leading-tight line-clamp-2',
                            isActive ? 'font-semibold' : 'font-normal'
                          )}
                        >
                          {title}
                        </span>
                        {/* Status Indicator */}
                        {conversationStatus === 'completed' && (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        )}
                        {conversationStatus === 'in_progress' && (
                          <Clock className="w-4 h-4 text-blue-600 flex-shrink-0 animate-pulse" />
                        )}
                      </div>

                      {/* Metadata Row */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground pl-6">
                        <span>{relativeTime}</span>
                        {recordCount > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-green-600 font-semibold">
                              {recordCount} record{recordCount > 1 ? 's' : ''}{' '}
                              created
                            </span>
                          </>
                        )}
                      </div>
                    </button>
                  )
                })}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area - Flexible width */}
        <div className="flex-1 flex flex-col bg-background">
          {/* Message List - Scrollable area (flex-1) */}
          <div className="flex-1 overflow-y-auto p-6">
            <MessageList messages={messages} />

            {/* Inline Compartment Form (appears when AI triggers form mode) */}
            {isCompartmentFormVisible && (
              <div className="mt-4 p-4 border rounded-lg bg-background">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold">
                    Configure Vehicle Compartments
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsCompartmentFormVisible(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <CompartmentItemsManager
                  value={compartmentItems}
                  onChange={setCompartmentItems}
                  projectId={projectId}
                  products={products || []}
                  showCapacityValidation={true}
                  className="mb-4"
                />

                <div className="flex gap-2">
                  <Button onClick={handleSaveCompartments}>Save Vehicle</Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsCompartmentFormVisible(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input - Fixed at bottom - 64px height (8 * 8pt) */}
          <div className="flex-shrink-0 border-t border-border">
            <ChatInput onSend={handleSend} disabled={isLoading} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
